import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { usePageTracking } from "./hooks/usePageTracking";
import { PresenceTracker } from "./components/PresenceTracker";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const AppPage = lazy(() => import("./pages/App"));
const ProjectWorkspace = lazy(() => import("./pages/ProjectWorkspace"));
const IntegrationsDocs = lazy(() => import("./pages/IntegrationsDocs"));
const WebhookSettings = lazy(() => import("./pages/WebhookSettings"));
const Settings = lazy(() => import("./pages/Settings"));
const Docs = lazy(() => import("./pages/Docs"));
const AddArtifact = lazy(() => import("./pages/AddArtifact"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const Billing = lazy(() => import("./pages/Billing"));

const queryClient = new QueryClient();

const PageTracker = ({ children }: { children: React.ReactNode }) => {
  usePageTracking();
  return (
    <>
      <PresenceTracker />
      {children}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageTracker>
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/users" element={<AdminUsers />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/app/project/:projectId" element={<ProjectWorkspace />} />
              <Route path="/app/project/:projectId/artifact/manual" element={<AddArtifact />} />
              <Route path="/app/settings" element={<Settings />} />
              <Route path="/app/settings/webhooks" element={<WebhookSettings />} />
              <Route path="/app/docs" element={<Docs />} />
              <Route path="/app/docs/integrations" element={<IntegrationsDocs />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/billing" element={<Billing />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </Suspense>
        </PageTracker>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
